import React, { useState } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { LISTINO, preventivo, euro } from "../utils/pianiNexiora";

const STEPS = [
  {
    id: "benvenuto",
    titolo: "Benvenuto in Nexiora 🔮",
    sottotitolo: "La piattaforma operativa per la tua azienda",
    contenuto: null,
  },
  {
    id: "livelli",
    titolo: "Come vuoi usare Nexiora?",
    sottotitolo: "Puoi partire in modo semplice e crescere nel tempo",
    contenuto: "livelli",
  },
  {
    id: "trasparenza",
    titolo: "Un principio fondamentale",
    sottotitolo: "Trasparenza sui dati",
    contenuto: "trasparenza",
  },
];

export default function OnboardingModal({ onClose }) {
  const { currentUser } = useAuth();
  const { effectiveOrgId } = useOrg();
  const [step, setStep] = useState(0);
  const [scelta, setScelta] = useState(null); // "base" | "completo"

  const segnaCompletato = async () => {
    if(!currentUser?.uid) return;
    try {
      await setDoc(doc(db,"onboarding",currentUser.uid),{
        completato:true, scelta, orgId:effectiveOrgId,
        completatoAt:serverTimestamp()
      });
    } catch(e){ console.warn("onboarding error:",e); }
    // Passaggio di mano alla checklist — un segnale leggero, solo per questa sessione, così
    // il primo passo si fa notare davvero invece di comparire identico a sempre.
    sessionStorage.setItem("nexiora_appena_chiuso_onboarding", "1");
    onClose();
  };

  const s = STEPS[step];
  const isUltimo = step===STEPS.length-1;
  const isPrimo = step===0;

  return (
    <div style={{
      position:"fixed",inset:0,zIndex:99000,
      background:"linear-gradient(135deg,#0f0c29,#302b63,#24243e)",
      display:"flex",alignItems:"center",justifyContent:"center",padding:20
    }}>
      <div style={{
        background:"#fff",borderRadius:20,width:"100%",maxWidth:620,
        boxShadow:"0 30px 80px rgba(0,0,0,.4)",overflow:"hidden",
        animation:"fadeInScale .3s ease"
      }}>
        {/* Progress bar */}
        <div style={{height:4,background:"#e5e7eb"}}>
          <div style={{height:4,background:"linear-gradient(90deg,#7c3aed,#a78bfa)",borderRadius:4,
            width:`${((step+1)/STEPS.length)*100}%`,transition:"width .4s ease"}}/>
        </div>

        {/* Header */}
        <div style={{background:"linear-gradient(135deg,#1e1b4b,#3730a3)",padding:"28px 32px 24px"}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
            <svg width="52" height="52" viewBox="0 0 72 72">
              <defs><linearGradient id="og" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#a78bfa"/><stop offset="100%" stopColor="#7c3aed"/>
              </linearGradient></defs>
              <polygon points="36,2 70,36 36,70 2,36" fill="url(#og)"/>
              <line x1="22" y1="24" x2="22" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
              <line x1="50" y1="24" x2="50" y2="48" stroke="white" strokeWidth="5" strokeLinecap="round"/>
              <line x1="22" y1="24" x2="50" y2="48" stroke="white" strokeWidth="4.5" strokeLinecap="round"/>
            </svg>
          </div>
          <h2 style={{fontSize:22,fontWeight:800,color:"#fff",textAlign:"center",marginBottom:6}}>{s.titolo}</h2>
          <p style={{fontSize:13,color:"rgba(255,255,255,.6)",textAlign:"center",margin:0}}>{s.sottotitolo}</p>
        </div>

        {/* Contenuto */}
        <div style={{padding:"28px 32px"}}>

          {/* Step 0 — Benvenuto */}
          {s.id==="benvenuto" && (
            <div>
              <p style={{fontSize:14,color:"#374151",lineHeight:1.7,marginBottom:16,textAlign:"center"}}>
                Nexiora è la tua piattaforma operativa aziendale. Gestisce commesse, team, economia, pianificazione e molto altro — tutto in un unico posto.
              </p>
              {/* Badge trial */}
              <div style={{background:"linear-gradient(135deg,#059669,#047857)",borderRadius:12,padding:"14px 20px",marginBottom:16,display:"flex",alignItems:"center",gap:14}}>
                <div style={{fontSize:32}}>🎁</div>
                <div>
                  <div style={{fontSize:14,fontWeight:800,color:"#fff"}}>{LISTINO.giorniProva} giorni di prova gratuita — tutti i moduli, anche Gold</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,.8)",marginTop:2}}>Accesso completo a tutte le funzionalità senza carta di credito. Nessun addebito automatico.</div>
                </div>
              </div>
              {/* Cosa succede dopo */}
              <div style={{background:"#f8fafc",border:"1px solid #e5e7eb",borderRadius:10,padding:"12px 16px",marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:700,color:"#374151",marginBottom:8,textTransform:"uppercase",letterSpacing:".05em"}}>Cosa succede dopo i {LISTINO.giorniProva} giorni</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[
                    {emoji:"✅",testo:"I dati inseriti restano tuoi e consultabili: non vengono cancellati alla fine della prova"},
                    {emoji:"⚡",testo:`Per continuare a lavorare scegli il piano Pro o Gold: da ${euro(preventivo(1,"pro").mensile)}/mese IVA esclusa fino a ${LISTINO.bloccoPosti} utenti, sconto del 10% con pagamento annuale`},
                    {emoji:"👁️",testo:"Senza un piano attivo Nexiora resta in sola lettura: consulti ed esporti tutto, ma non puoi inserire o modificare"},
                    {emoji:"📞",testo:"Nessun blocco improvviso: negli ultimi 14 giorni l'app ti avvisa della scadenza"},
                  ].map(({emoji,testo})=>(
                    <div key={testo} style={{display:"flex",gap:10,alignItems:"flex-start",fontSize:12,color:"#374151"}}>
                      <span style={{flexShrink:0}}>{emoji}</span><span>{testo}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10}}>
                {[
                  {emoji:"📁",label:"Commesse e progetti"},
                  {emoji:"👥",label:"Team e presenze"},
                  {emoji:"📊",label:"Analisi e KPI"},
                  {emoji:"🔮",label:"Nexiora Insight"},
                  {emoji:"📅",label:"Pianificazione"},
                  {emoji:"💳",label:"Controllo economico"},
                ].map(({emoji,label})=>(
                  <div key={label} style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"10px 6px",textAlign:"center"}}>
                    <div style={{fontSize:20,marginBottom:4}}>{emoji}</div>
                    <div style={{fontSize:10,fontWeight:600,color:"#374151"}}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 1 — Livelli */}
          {s.id==="livelli" && (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              {[
                {
                  id:"base", emoji:"🚀", titolo:"Utilizzo base",
                  col:"#2563eb", bg:"#dbeafe",
                  voci:["Progetti e commesse","Attività e checklist","Database clienti","Database dipendenti","Chat e notifiche","Verbali e rapportini"],
                  desc:"Perfetto per iniziare. Nessuna configurazione avanzata richiesta."
                },
                {
                  id:"completo", emoji:"⚡", titolo:"Utilizzo completo",
                  col:"#7c3aed", bg:"#ede9fe",
                  voci:["Tutto il base +","Capacità Produttiva","Motore economico","Nexiora Insight","KPI e analisi","Simulatore commesse"],
                  desc:"Richiede di compilare piano di carico, ruoli e programmazione. Analisi molto più precise."
                }
              ].map(opt=>(
                <div key={opt.id} onClick={()=>setScelta(opt.id)}
                  style={{border:`2px solid ${scelta===opt.id?opt.col:"#e5e7eb"}`,borderRadius:14,padding:20,cursor:"pointer",
                    background:scelta===opt.id?opt.bg:"#fff",transition:"all .2s"}}>
                  <div style={{fontSize:28,marginBottom:8}}>{opt.emoji}</div>
                  <div style={{fontSize:15,fontWeight:800,color:scelta===opt.id?opt.col:"#111827",marginBottom:8}}>{opt.titolo}</div>
                  <ul style={{margin:0,padding:"0 0 0 16px",fontSize:12,color:"#374151",lineHeight:2}}>
                    {opt.voci.map(v=><li key={v}>{v}</li>)}
                  </ul>
                  <div style={{marginTop:10,fontSize:11,color:scelta===opt.id?opt.col:"#9ca3af",fontStyle:"italic"}}>{opt.desc}</div>
                  {scelta===opt.id&&<div style={{marginTop:8,fontSize:11,fontWeight:700,color:opt.col}}>✓ Selezionato</div>}
                </div>
              ))}
            </div>
          )}

          {/* Step 2 — Trasparenza */}
          {s.id==="trasparenza" && (
            <div>
              <div style={{background:"#1e1b4b",borderRadius:14,padding:24,marginBottom:20,textAlign:"center"}}>
                <div style={{fontSize:32,marginBottom:12}}>💡</div>
                <p style={{fontSize:15,color:"#fff",lineHeight:1.8,fontStyle:"italic",margin:0}}>
                  "Nexiora elabora automaticamente tutto ciò che viene gestito al suo interno. La qualità delle analisi dipende dalla qualità e dall'aggiornamento dei dati inseriti."
                </p>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {[
                  {emoji:"📊",titolo:"Analisi accurate",desc:"Pianificazione, saturazione e KPI sono affidabili solo per le commesse gestite e aggiornate in Nexiora."},
                  {emoji:"⚠️",titolo:"Avvisi di trasparenza",desc:"Nexiora ti segnala quando i dati sono incompleti e spiega l'impatto sulle analisi."},
                  {emoji:"📈",titolo:"Migliora nel tempo",desc:"Più dati inserisci, più le analisi diventano precise. Nexiora cresce con la tua azienda."},
                ].map(({emoji,titolo,desc})=>(
                  <div key={titolo} style={{display:"flex",gap:14,alignItems:"flex-start",padding:"12px 14px",background:"#f8fafc",borderRadius:10,border:"1px solid #e5e7eb"}}>
                    <span style={{fontSize:22,flexShrink:0}}>{emoji}</span>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:"#111827",marginBottom:3}}>{titolo}</div>
                      <div style={{fontSize:12,color:"#6b7280",lineHeight:1.5}}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{marginTop:16,background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"12px 16px",display:"flex",gap:10,alignItems:"flex-start"}}>
                <span style={{fontSize:18,flexShrink:0}}>👇</span>
                <div style={{fontSize:11.5,color:"#5b21b6",lineHeight:1.6}}>
                  Chiudendo questa schermata trovi subito una checklist con i primi passi. Il più veloce per vedere Nexiora all'opera: dati aziendali, un cliente, e la tua prima commessa — pochi minuti, e vedi già qualcosa di vivo, non solo campi da compilare.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer navigazione */}
        <div style={{padding:"16px 32px 28px",display:"flex",alignItems:"center",justifyContent:"space-between",borderTop:"1px solid #f3f4f6"}}>
          <div style={{display:"flex",gap:6}}>
            {STEPS.map((_,i)=>(
              <div key={i} style={{width:i===step?20:8,height:8,borderRadius:4,background:i===step?"#7c3aed":"#e5e7eb",transition:"all .3s"}}/>
            ))}
          </div>
          <div style={{display:"flex",gap:10}}>
            {!isPrimo&&(
              <button onClick={()=>setStep(s=>s-1)}
                style={{padding:"9px 20px",border:"1px solid #e5e7eb",borderRadius:9,background:"#fff",color:"#374151",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                ← Indietro
              </button>
            )}
            {!isUltimo?(
              <button onClick={()=>setStep(s=>s+1)}
                disabled={s.id==="livelli"&&!scelta}
                style={{padding:"9px 24px",border:"none",borderRadius:9,background:s.id==="livelli"&&!scelta?"#d1d5db":"#7c3aed",color:"#fff",fontSize:13,fontWeight:700,cursor:s.id==="livelli"&&!scelta?"not-allowed":"pointer",fontFamily:"'Inter',sans-serif"}}>
                Continua →
              </button>
            ):(
              <button onClick={segnaCompletato}
                style={{padding:"9px 24px",border:"none",borderRadius:9,background:"#059669",color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                ✅ Inizia con Nexiora
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes fadeInScale{from{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}`}</style>
    </div>
  );
}
